/**
 * The copy-pasteable POST /v1/batches for one pipeline, shown while a team
 * has no batches so the first one can come from a script as easily as
 * from the New batch dialog. The body carries the pipeline's own default
 * options (from its published schema) and the team's S3 connection id
 * when it has one, so the only edits left are the two paths and the key.
 */

import type { BatchCreate, PipelineInfo } from '@/api/generated/model'
import { PUBLIC_API_BASE } from '@/lib/env'

import { initialOptionValues, optionFieldsFromSchema, optionsForSubmit } from './batchOptions'

/** Stands in for the connection id while the team has no S3 connection. */
export const CONNECTION_ID_PLACEHOLDER = '<connection-id>'

export type SnippetPipeline = Pick<PipelineInfo, 'name' | 'options_schema'>

// Same base as the evals and spend snippets: local, test or prod, whichever
// this deployment serves. The paths mirror the New batch dialog's placeholders.
export function batchCurlSnippet(pipeline: SnippetPipeline, connectionId: string | null): string {
  const fields = optionFieldsFromSchema(pipeline.options_schema)
  const body: BatchCreate = {
    pipeline: pipeline.name,
    connection_id: connectionId ?? CONNECTION_ID_PLACEHOLDER,
    input_path: 's3://bucket/documents/',
    output_path: 's3://bucket/redacted/',
    options: optionsForSubmit(fields, initialOptionValues(fields)),
  }
  // Close and reopen the shell string around any apostrophe (an option
  // default like customer's would otherwise end the -d argument early), then
  // indent the body under `-d '` so the whole request reads as one block.
  const json = JSON.stringify(body, null, 2)
    .replace(/'/g, "'\\''")
    .replace(/\n/g, '\n  ')
  return `curl -X POST ${PUBLIC_API_BASE}/v1/batches \\
  -H "Authorization: Bearer $TRAINLOOP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${json}'`
}
